import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * EntertainmentCarousel - Composant de carrousel de divertissement
 * 
 * Affiche des blagues et faits éducatifs adaptés à l'âge des enfants
 * pendant la génération des histoires.
 * 
 * Fonctionnalités:
 * - Rotation automatique toutes les 10 secondes
 * - Navigation manuelle avec boutons gauche/droite
 * - Préchargement de 3 items + 1 par swipe avant
 * - Sélection basée sur l'âge des enfants
 */
const EntertainmentCarousel = ({ children, isVisible = true }) => {
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadedChildrenRef = useRef(null);  // Ref pour tracker les enfants chargés

  // Charger 30 items depuis l'API
  const loadItems = useCallback(async () => {
    console.log('🎪 EntertainmentCarousel: Chargement de 30 items...', { children, childrenCount: children?.length });
    
    if (!children || children.length === 0) {
      console.warn('⚠️  Aucun enfant fourni au carrousel');
      setError("Aucun enfant sélectionné");
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Préparer la liste des enfants avec leurs âges
      const childrenData = children.map(child => ({
        name: child.name || 'Enfant',
        age: child.age || 5,
        id: child.id
      }));

      console.log('📤 Appel API pour 30 items:', `${BACKEND_URL}/api/entertainment/get-items`);

      const requestConfig = {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 15000  // Timeout de 15 secondes pour 30 items
      };
      
      // Ajouter le token seulement s'il existe
      if (token) {
        requestConfig.headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/entertainment/get-items`,
        { children: childrenData },
        requestConfig
      );

      console.log('📥 Réponse API:', response.status, `${response.data.items?.length || 0} items reçus`);

      if (response.data && response.data.items) {
        console.log(`✅ ${response.data.items.length} items chargés avec succès`);
        setItems(response.data.items);
        setCurrentIndex(0);
        setError(null);
      } else {
        throw new Error("Format de réponse invalide");
      }
    } catch (err) {
      console.error("❌ Erreur chargement items divertissement:", err);
      // Ne pas afficher d'erreur visible - utiliser silencieusement les items par défaut
      console.log("ℹ️ Utilisation des items par défaut (erreur réseau ignorée)");
      // Items par défaut variés en cas d'erreur
      setItems([
        {
          id: '1',
          content: "Pourquoi le crayon est triste ?\nParce qu'il a perdu sa mine !",
          type: 'joke',
          age_range: '4-7 ans',
          for_child: 'Toi'
        },
        {
          id: '2',
          content: "Le saviez-vous ? Les chats ronronnent quand ils sont contents. 😺",
          type: 'fact',
          age_range: '2-4 ans',
          for_child: 'Toi'
        },
        {
          id: '3',
          content: "Que dit un zéro à un huit ?\nJolie ceinture !",
          type: 'joke',
          age_range: '7-11 ans',
          for_child: 'Toi'
        },
        {
          id: '4',
          content: "Le saviez-vous ? Les éléphants sont les seuls animaux qui ne peuvent pas sauter !",
          type: 'fact',
          age_range: '5-8 ans',
          for_child: 'Toi'
        },
        {
          id: '5',
          content: "Qu'est-ce qu'un canif ?\nUn petit fien !",
          type: 'joke',
          age_range: '6-10 ans',
          for_child: 'Toi'
        }
      ]);
      setError(null); // Ne pas montrer l'erreur à l'utilisateur
    } finally {
      setLoading(false);
    }
  }, [children]);

  // Charger les items au montage et quand les enfants changent RÉELLEMENT
  useEffect(() => {
    if (!isVisible) return;
    
    // Vérifier si les enfants ont réellement changé
    const childrenIds = children?.map(c => c.id).sort().join(',') || '';
    const loadedIds = loadedChildrenRef.current || '';
    
    if (childrenIds !== loadedIds) {
      console.log('🔄 Enfants ont changé, rechargement...', { from: loadedIds, to: childrenIds });
      loadedChildrenRef.current = childrenIds;
      loadItems();
    } else {
      console.log('✅ Enfants inchangés, pas de rechargement');
    }
  }, [isVisible, children, loadItems]);

  // Navigation - Suivant (simple, sans chargement progressif)
  const handleNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % items.length);
  }, [items.length]);

  // Navigation - Précédent
  const handlePrevious = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  // Rotation automatique toutes les 10 secondes
  useEffect(() => {
    if (!isVisible || items.length === 0) return;

    const interval = setInterval(() => {
      handleNext();
    }, 10000); // 10 secondes

    return () => clearInterval(interval);
  }, [isVisible, items.length, handleNext]);

  // Si le carrousel n'est pas visible
  if (!isVisible) {
    return null;
  }

  // État de chargement
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <p className="ml-3 text-gray-600">Chargement des surprises...</p>
      </div>
    );
  }

  // Affichage de l'erreur pour debug (visible sur iPhone)
  if (error && items.length === 0) {
    return (
      <div className="text-center p-8 bg-yellow-50 rounded-2xl">
        <p className="text-red-600 font-medium mb-2">⚠️ Erreur de chargement</p>
        <p className="text-sm text-gray-600">{error}</p>
        <p className="text-xs text-gray-500 mt-2">
          Enfants: {children?.length || 0} | 
          Token: {localStorage.getItem('token') ? '✅' : '❌'}
        </p>
      </div>
    );
  }

  // Aucun item
  if (items.length === 0) {
    return (
      <div className="text-center p-8 text-gray-500">
        <p>Aucun contenu disponible</p>
        <p className="text-xs mt-2">Children: {children?.length || 0}</p>
      </div>
    );
  }

  const currentItem = items[currentIndex];
  const isJoke = currentItem.type === 'joke';

  return (
    <div className="entertainment-carousel relative w-full max-w-2xl mx-auto">
      {/* Conteneur principal du carrousel */}
      <div className="relative bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl shadow-lg p-8 min-h-[200px] flex items-center justify-center">
        
        {/* Bouton Précédent */}
        <button
          onClick={handlePrevious}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all duration-200 hover:scale-110"
          aria-label="Élément précédent"
        >
          <ChevronLeft className="w-5 h-5 text-indigo-600" />
        </button>

        {/* Contenu de l'item actuel */}
        <div className="text-center px-12 animate-fade-in">
          {/* Badge de type */}
          <div className="mb-4">
            <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium ${
              isJoke 
                ? 'bg-yellow-100 text-yellow-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              {isJoke ? '🤣 Blague' : '🧠 Le saviez-vous ?'}
            </span>
          </div>

          {/* Contenu principal */}
          <div className="text-lg md:text-xl text-gray-800 leading-relaxed whitespace-pre-wrap">
            {currentItem.content}
          </div>

          {/* Informations supplémentaires */}
          <div className="mt-6 flex items-center justify-center gap-4 text-sm text-gray-500">
            <span>📚 {currentItem.age_range}</span>
            {currentItem.for_child && (
              <span>👤 Pour {currentItem.for_child}</span>
            )}
          </div>
        </div>

        {/* Bouton Suivant */}
        <button
          onClick={handleNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all duration-200 hover:scale-110"
          aria-label="Élément suivant"
        >
          <ChevronRight className="w-5 h-5 text-indigo-600" />
        </button>
      </div>

      {/* Indicateurs de pagination */}
      <div className="flex justify-center items-center gap-2 mt-4">
        {items.slice(0, Math.min(5, items.length)).map((_, idx) => (
          <div
            key={idx}
            className={`h-2 rounded-full transition-all duration-300 ${
              idx === currentIndex 
                ? 'w-8 bg-indigo-600' 
                : 'w-2 bg-gray-300'
            }`}
          />
        ))}
        {items.length > 5 && (
          <div className="text-xs text-gray-500 ml-2">
            {currentIndex + 1}/{items.length}
          </div>
        )}
      </div>

      {/* Style pour l'animation */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EntertainmentCarousel;
